
import React, { useState } from 'react';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import { FullScreenLayout } from '../../../components/FullScreenLayout';
import EmailInputElement from '../../../components/inputs/EmailInputElement';
import TextInputElement from '../../../components/inputs/TextInputElement';
import { getCurrentEnvironment } from '../../../utils/config';
import { Environment } from '../../../utils/Enums';
import { useCreateStripeItem } from '../hooks/useCreateStripeItem';

const StripePurchasesPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    numberOfMonths: ''
  });
  
  const [errors, setErrors] = useState({
    email: '',
    numberOfMonths: ''
  });
  
  const { createStripeItem, isLoading, error, success } = useCreateStripeItem();

  // Validation function that sets errors state
  const validateForm = () => {
    const newErrors = {
      email: '',
      numberOfMonths: ''
    };

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email è richiesta';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato email non valido';
    }

    // Number of months validation
    if (!formData.numberOfMonths) {
      newErrors.numberOfMonths = 'Numero di mesi è richiesto';
    } else if (!/^\d+$/.test(formData.numberOfMonths) || parseInt(formData.numberOfMonths) <= 0) {
      newErrors.numberOfMonths = 'Inserisci un numero valido di mesi';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  // Check if form is valid for button state (without setting errors)
  const isFormValid = () => {
    // Check if all fields have values
    if (!formData.email || !formData.numberOfMonths) {
      return false;
    }

    // Check if there are any current errors
    if (Object.values(errors).some(error => error !== '')) {
      return false;
    }

    // Validate formats without setting state
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
    const monthsValid = /^\d+$/.test(formData.numberOfMonths) && parseInt(formData.numberOfMonths) > 0;

    return emailValid && monthsValid;
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const getProductId = () => {
    const environment = getCurrentEnvironment();
    
    // Product IDs for SUB_METABOLIC_HEALTH
    const productIds = {
      [Environment.DEVELOP]: "7415324934234", // Dev
      [Environment.STAGE]: "7415324934234", // Using dev for stage
      [Environment.PRODUCTION]: "10080989413640" // Prod
    };
    
    return productIds[environment] || productIds[Environment.DEVELOP];
  };

  const generateRandomId = () => {
    // Generate a random 13-digit number (typical Shopify ID length)
    return Math.floor(Math.random() * 9000000000000) + 1000000000000;
  };

  const createApiBody = () => {
    const numberOfMonths = parseInt(formData.numberOfMonths);
    const productId = getProductId();
    
    // Generate random IDs for each submission
    const orderId = generateRandomId();
    const variantId = generateRandomId();
    const contractId = generateRandomId();
    const customerId = generateRandomId();
    
    return {
      data: {
        sku: null,
        price: 0,
        currency: "EUR",
        discount: 0,
        duration: `EVERY ${numberOfMonths} MONTH${numberOfMonths > 1 ? 'S' : ''}`,
        interval: "MONTH",
        order_id: `gid://shopify/Order/${orderId}`,
        quantity: 1,
        item_type: "recurring",
        created_at: new Date().toISOString(),
        is_renewal: false,
        product_id: `gid://shopify/Product/${productId}`,
        updated_at: new Date().toISOString(),
        variant_id: `gid://shopify/ProductVariant/${variantId}`,
        contract_id: `gid://shopify/SubscriptionContract/${contractId}`,
        customer_id: `gid://shopify/Customer/${customerId}`,
        product_name: "Percorso Medico e Nutrizionale Completo",
        contract_note: null,
        customer_mail: formData.email,
        interval_count: numberOfMonths,
        contract_status: "ACTIVE",
        shipping_address: {
          zip: "",
          city: "",
          phone: "",
          company: null,
          country: "Italy",
          address1: "",
          address2: null,
          province: "",
          last_name: "",
          time_zone: "Europe/Rome",
          first_name: "",
          country_code: "IT",
          province_code: "TN"
        }
      }
    };
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Create the API body with form data
      const apiBody = createApiBody();
      console.log('API Body to be sent:', JSON.stringify(apiBody, null, 2));
      
      // Call the API using the hook
      await createStripeItem(apiBody);
      
      // Reset form on success
      setFormData({
        email: '',
        numberOfMonths: ''
      });
      
      alert('Acquisto aggiunto con successo!');
      
    } catch (err) {
      console.error('Error adding purchase:', err);
      alert('Errore durante l\'aggiunta dell\'acquisto');
    }
  };

  return (
    <FullScreenLayout 
      title="Gestione Acquisti Stripe"
      showBackButton={true}
      backButtonText="Torna indietro"
    >
      <div className="w-full max-w-2xl mx-auto space-y-6">
        {/* Disclaimer */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-800 font-medium">
                Stai aggiungendo l'acquisto "sub-metabolic-health"
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            Aggiungi Nuovo Acquisto
          </h2>
          
          <form className="space-y-6">
            {/* Email Field */}
            <EmailInputElement
              name="email"
              label="Email Cliente"
              placeholder="Inserisci l'email del cliente"
              inputValue={formData.email}
              onChange={handleInputChange('email')}
              errorText={errors.email}
            />
            
            {/* Number of Months Field */}
            <TextInputElement
              name="numberOfMonths"
              label="Numero di Mesi"
              placeholder="Inserisci il numero di mesi (es. 6)"
              inputValue={formData.numberOfMonths}
              onChange={handleInputChange('numberOfMonths')}
              showLabel={true}
              errorText={errors.numberOfMonths}
            />
            
            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800 font-medium">
                      Errore: {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Success Display */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-800 font-medium">
                      Acquisto aggiunto con successo!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4 h-[50px]">
              <PrimaryButton
                text="Aggiungi Acquisto"
                onClick={handleSubmit}
                pointer={isFormValid()}
                disabled={!isFormValid() || isLoading}
                isLoading={isLoading}
                fontSize={16}
              />
            </div>
          </form>
        </div>
        
        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Istruzioni:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Email:</strong> Inserisci l'email del cliente che ha effettuato l'acquisto</li>
            <li>• <strong>Numero di Mesi:</strong> Inserisci il numero di mesi della sottoscrizione (es. 6)</li>
          </ul>
        </div>
      </div>
    </FullScreenLayout>
  );
};

export default StripePurchasesPage;
